import moment from 'moment-timezone';
import get from 'lodash/get';

// Supported date formats for parsing
const DATE_FORMATS = [
  'YYYY-MM-DD',
  'DD-MM-YYYY',
  'DD/MM/YYYY',
  'D/M/YYYY',
  'M-D-YYYY',
  'D-M-YYYY',
  'YYYY/M/DD',
  'YYYY/MM/DD'
];

/**
 * Convert date string to moment object with multiple format support
 * Handles placeholder values like "ADMIN FILL INFORMATION HERE"
 */
const convertToMoment = (dateString) => {
  if (!dateString || typeof dateString !== 'string' || dateString.trim() === '') {
    return null;
  }

  // Check for placeholder values
  const upperStr = dateString.toUpperCase();
  if (upperStr.includes('ADMIN') || upperStr.includes('FILL')) {
    return null;
  }

  for (const format of DATE_FORMATS) {
    const parsedDate = moment(dateString, format, true);
    if (parsedDate.isValid()) {
      return parsedDate;
    }
  }

  return null;
};

/**
 * Calculate years of experience from a date string
 * @param {string} dateString - Date string to calculate from
 * @returns {number|null} - Years of experience or null
 */
const getExperienceFromDate = (dateString) => {
  const momentDate = convertToMoment(dateString);
  if (!momentDate) return null;
  return moment().diff(momentDate, 'years');
};

/**
 * Calculate driving experience from user profile
 * Considers multiple data sources and returns maximum experience found
 * @param {Object} user - User object
 * @param {boolean} includeForeign - Whether to include foreign license data (default: true)
 *                                   Set to false for HPV restrictions (Singapore only)
 * Returns 0 if no valid data found
 */
export const calculateDrivingExperience = (user, includeForeign = true) => {
  try {
    const experiences = [];

    // Helper to add valid experience to array
    const addExperience = (dateString) => {
      const exp = getExperienceFromDate(dateString);
      if (exp !== null) experiences.push(exp);
    };

    // Source 1 & 2: protectedData.drivingLicense (Singapore and Foreign)
    const drivingLicenseObj = get(user, 'attributes.profile.protectedData.drivingLicense', null);
    if (drivingLicenseObj) {
      addExperience(drivingLicenseObj.drivingLicenseIssue);
      if (includeForeign) {
        addExperience(drivingLicenseObj.drivingLicenseIssueForeign);
      }
    }

    // Source 3: privateData.numberOfYearsOfDrivingLicenseExperience (direct value)
    const directYears = get(user, 'attributes.profile.privateData.numberOfYearsOfDrivingLicenseExperience', null);
    if (directYears !== null && directYears !== undefined) {
      experiences.push(Number(directYears));
    }

    // Source 4 & 5: privateData registration dates
    addExperience(get(user, 'attributes.profile.privateData.drivingLicenseRegistrationDate', null));
    if (includeForeign) {
      addExperience(get(user, 'attributes.profile.privateData.drivingForeignLicenseRegistrationDate', null));
    }

    // Source 6: Onfido License Document Report
    const onfidoLicenseReport = get(user, 'attributes.profile.metadata.onfidoLicenseDocumentReport', null);
    if (onfidoLicenseReport && onfidoLicenseReport.properties) {
      const { documentType, issuingCountry, issuingDate } = onfidoLicenseReport.properties;
      // Only include if it's a driving license AND (includeForeign OR Singapore)
      if (documentType === 'driving_licence' && (includeForeign || issuingCountry === 'SGP')) {
        addExperience(issuingDate);
      }
    }

    // Source 7: Onfido NRIC Document Report (Singapore only)
    const onfidoNRICReport = get(user, 'attributes.profile.metadata.onfidoNRICDocumentReport', null);
    if (onfidoNRICReport && onfidoNRICReport.properties) {
      const { issuingCountry, issuingDate } = onfidoNRICReport.properties;
      if (onfidoNRICReport.name === 'document_with_address_information' && issuingCountry === 'SGP') {
        addExperience(issuingDate);
      }
    }

    // Return maximum experience or 0 if none found
    return experiences.length > 0 ? Math.max(...experiences) : 0;
  } catch (e) {
    console.log('Error calculating driving experience:', e);
    return 0;
  }
};
